import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useUserAutocompleteQuery } from '../../../../queries/Admin/Account/useUserAutocompleteQuery';
import {
	Autocomplete,
	AutocompleteContent,
	AutocompleteControl,
	AutocompleteInput,
	AutocompleteItem,
	AutocompleteList,
} from '../../../../web/components-v2/Autocomplete';
import { Button } from '../../../../web/components-v2/Button';
import { DateInput } from '../../../../web/components-v2/DateInput';
import { Input } from '../../../../web/components-v2/Input';
import { ActionItemsIcon } from '../../../../web/components/svgs/icons/ActionItemsIcon';
import { XIcon } from '../../../../web/components/svgs/icons/XIcon';
import { success } from '../../../../web/styles/colors';
import { meetingActionItemsStyles as s } from './meetingActionItemsStyles';

/** A single in-progress action item held in composer state (not the API shape). */
export interface IActionItemDraft {
	/** Stable list key — never sent to the API. */
	key: number;
	/** The action-item description. */
	text: string;
	/** Optional assignee. */
	assignee?: Api.IUser;
	/** Optional due date as `YYYY-MM-DD`. */
	dueDate: string;
}

/** Create an empty action-item draft with a key one past the current max. */
export const newActionItemDraft = (items: IActionItemDraft[]): IActionItemDraft => ({
	key: items.length ? Math.max(...items.map(i => i.key)) + 1 : 1,
	text: '',
	dueDate: '',
});

/**
 * Convert action-item drafts to the API shape used on `note.actionItems` (meeting + plain notes):
 * drop blank rows, wrap the text as rich-text content, always public + the three ref arrays, an
 * id-only assignee, and a full ISO due date. Shared by both note composers.
 */
export const actionItemDraftsToApi = (items: IActionItemDraft[]): Api.IActionItem[] =>
	items
		.filter(item => item.text.trim())
		.map(
			(item): Api.IActionItem => ({
				content: Api.createRawRichTextContentStateWithHtmlStringValue(item.text),
				visibility: 'all',
				referencedEntities: { companies: [], contacts: [], users: [] },
				...(item.assignee ? { assignee: { id: item.assignee.id } as Api.IUser } : {}),
				...(item.dueDate ? { dueDate: moment(item.dueDate).toISOString() } : {}),
			})
		);

const userDisplayName = (user: Api.IUser): string =>
	`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.primaryEmail?.value || user.id;

/** Debounced people typeahead for an action item's assignee (mirrors the General card's field). */
const AssigneeField = ({ assignee, onSelect }: { assignee?: Api.IUser; onSelect: (user: Api.IUser) => void }) => {
	const [inputValue, setInputValue] = useState(assignee ? userDisplayName(assignee) : '');
	const [debouncedFragment, setDebouncedFragment] = useState('');

	useEffect(() => {
		const handle = setTimeout(() => setDebouncedFragment(inputValue), 250);
		return () => clearTimeout(handle);
	}, [inputValue]);

	const { data } = useUserAutocompleteQuery({ fragment: debouncedFragment, preventImpersonation: true });
	const users = data?.values ?? [];

	return (
		<Autocomplete items={users} value={inputValue} onValueChange={setInputValue}>
			<AutocompleteControl styles={[s.autocompleteControl]}>
				<AutocompleteInput placeholder='Assign to…' />
			</AutocompleteControl>
			<AutocompleteContent>
				<AutocompleteList>
					{users.map(user => (
						<AutocompleteItem
							key={user.id}
							value={user.id}
							onClick={() => {
								setInputValue(userDisplayName(user));
								onSelect(user);
							}}
						>
							{userDisplayName(user)}
						</AutocompleteItem>
					))}
				</AutocompleteList>
			</AutocompleteContent>
		</Autocomplete>
	);
};

const ActionItemRow = ({
	item,
	onChange,
	onRemove,
}: {
	item: IActionItemDraft;
	onChange: (patch: Partial<IActionItemDraft>) => void;
	onRemove: () => void;
}) => (
	<div className={css(s.row)}>
		<div className={css(s.rowTop)}>
			<Input
				styles={[s.textInput]}
				value={item.text}
				placeholder='Type action item…'
				onChange={e => onChange({ text: e.target.value })}
			/>
			<Button variant='ghost' size='icon' styles={[s.removeButton]} onClick={onRemove} aria-label='Remove action item'>
				<XIcon width={12} height={12} />
			</Button>
		</div>
		<div className={css(s.rowBottom)}>
			<AssigneeField assignee={item.assignee} onSelect={user => onChange({ assignee: user })} />
			<DateInput value={item.dueDate} onChange={value => onChange({ dueDate: value })} />
		</div>
	</div>
);

export interface IMeetingActionItemsProps {
	/** The current action-item drafts. */
	items: IActionItemDraft[];
	/** Reports the next draft list (add / edit / remove). */
	onChange: (items: IActionItemDraft[]) => void;
	/** Hide the built-in "Add action item" button when the parent owns adding (e.g. a footer link). */
	hideAddButton?: boolean;
}

/**
 * Controlled editor for a meeting note's action items: a list of rows (description + assignee +
 * due date) plus an "Add action item" affordance. Purely presentational — the parent owns the
 * draft list and maps it to `Api.IActionItem[]` on save.
 */
export const MeetingActionItems = ({ items, onChange, hideAddButton }: IMeetingActionItemsProps) => {
	const patchItem = (key: number, patch: Partial<IActionItemDraft>) =>
		onChange(items.map(item => (item.key === key ? { ...item, ...patch } : item)));

	return (
		<div className={css(s.container)}>
			{items.map(item => (
				<ActionItemRow
					key={item.key}
					item={item}
					onChange={patch => patchItem(item.key, patch)}
					onRemove={() => onChange(items.filter(i => i.key !== item.key))}
				/>
			))}
			{!hideAddButton && (
				<Button variant='link' styles={[s.addButton]} onClick={() => onChange([...items, newActionItemDraft(items)])}>
					<ActionItemsIcon fillColor={success} width={16} height={16} />
					<span>Add action item</span>
				</Button>
			)}
		</div>
	);
};
